// Faixa global de ambiente QA (portada da referência HelpCor HCOR-63222).
//
// Detecção por HOSTNAME em runtime, não por env de build: a imagem de QA é o
// mesmo `vite build` de produção (import.meta.env.MODE === 'production' nos
// dois deploys), então nada vindo do build distingue QA de produção. O
// hostname é a única diferença garantida entre os ambientes
// (priceshow.qa.trademarketingforce.com vs produção) e não depende de
// env/args de build chegarem intactos ao container.
import { useEffect, useState } from "react";
import { TriangleAlert } from "lucide-react";

export function isQaEnvironment(
  hostname: string = window.location.hostname,
): boolean {
  return /(^|\.)qa\./i.test(hostname);
}

// [PSHWO-63227] Formata o restored_at do marcador de restore para exibição
// ("DD/MM/AAAA às HH:mm" — só a data não distingue dois restores no mesmo
// dia; hora local do navegador). Exportada para teste; devolve null para
// entrada ausente/inválida — a faixa simplesmente omite a data (produção e
// QA sem marcador caem aqui).
export function formatRestoreDate(
  isoString: string | null | undefined,
): string | null {
  if (!isoString) return null;
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${date} às ${time}`;
}

// [PSHWO-63227] URL do commit deployado no Forgejo (porte de HCOR-63233).
// O backend só devolve `commit` depois de validar o env GIT_COMMIT por
// regex, mas o valor vira href — validamos de novo aqui (defesa em
// profundidade contra resposta adulterada): qualquer coisa que não seja um
// sha hex 7-40 devolve null e a faixa omite o build.
export function buildCommitUrl(
  commit: string | null | undefined,
): string | null {
  if (!commit || !/^[0-9a-f]{7,40}$/.test(commit)) return null;
  return `https://git.trademarketingforce.com/tarcisio/Priceshow/commit/${commit}`;
}

// Fundo amarelo do design system (--color-warning, idêntico em light/dark de
// propósito — aviso de ambiente não muda de cara com o tema) com texto
// quase-preto (--color-warning-foreground): contraste alto em 12px bold.
//
// `hostname` é injetável só para teste (jsdom = localhost); em produção o
// default de isQaEnvironment (window.location.hostname) decide.
export function QaEnvironmentBanner({ hostname }: { hostname?: string } = {}) {
  const active = isQaEnvironment(hostname);
  // [PSHWO-63227] Data do último restore do banco, gravada pela rotina de
  // restore e servida por GET /api/qa/data-info (público, sem dado sensível;
  // o nginx do admin remove o prefixo /api/ antes de repassar ao FastAPI).
  // fetch só quando a faixa está ativa; qualquer falha (endpoint antigo,
  // marcador ausente, rede) apenas omite a data — nunca quebra a faixa.
  const [restoreDate, setRestoreDate] = useState<string | null>(null);
  // [PSHWO-63227] sha do commit deployado (GIT_COMMIT do container da API);
  // null em produção/dev — a faixa só acrescenta o build quando presente.
  const [commit, setCommit] = useState<string | null>(null);

  useEffect(() => {
    if (!active) return undefined;
    const controller = new AbortController();
    fetch("/api/qa/data-info", { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then(
        (
          info: { restored_at?: string | null; commit?: string | null } | null,
        ) => {
          setRestoreDate(formatRestoreDate(info?.restored_at));
          setCommit(info?.commit ?? null);
        },
      )
      .catch(() => {});
    return () => controller.abort();
  }, [active]);

  if (!active) return null;

  const commitUrl = buildCommitUrl(commit);

  return (
    <div
      role="status"
      aria-label="Ambiente de QA"
      className="fixed inset-x-0 bottom-0 z-[2000] flex items-center justify-center gap-2 bg-warning px-3 py-1 text-xs font-bold uppercase tracking-wide text-warning-foreground"
    >
      <TriangleAlert size={14} aria-hidden="true" />
      <span>
        Ambiente de QA — dados de teste, não use dados reais
        {restoreDate ? ` · dados restaurados em ${restoreDate}` : ""}
        {commitUrl ? " · build " : ""}
        {commitUrl && commit && (
          // normal-case: o sha é hex minúsculo; o uppercase da faixa
          // distorceria o texto do link (o href já está correto sempre).
          <a
            href={commitUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="normal-case text-inherit underline"
            title="Abrir o commit deployado no Forgejo"
          >
            {commit.slice(0, 9)}
          </a>
        )}
      </span>
    </div>
  );
}
